import { supabase } from '@/lib/supabase';
import { Parent } from '@/types/models';
import { toast } from 'sonner';
import { mapParentFromSupabase } from '../mappers/entityMappers';
import { formatPhoneNumber } from '@/lib/format';

export const getParentsByFamilyId = async (familyId: string): Promise<Parent[]> => {
  try {
    const { data, error } = await supabase
      .from('parents')
      .select('*')
      .eq('family_id', familyId);

    if (error) {
      console.error('Error fetching parents:', error);
      throw error;
    }
    
    return (data || []).map(parentData => mapParentFromSupabase(parentData));
  } catch (error) {
    console.error('Error fetching parents:', error);
    toast.error('Failed to load parents');
    return [];
  }
};

export const createParent = async (
  parentData: Omit<Parent, 'id' | 'created_at'>
): Promise<Parent | null> => {
  try {
    console.log("Creating parent with data:", parentData);
    
    // Ensure we have all required fields
    if (!parentData.family_id) {
      console.error('Missing family_id in parent data');
      toast.error('Failed to create parent: Missing family ID');
      return null;
    }
    
    if (!parentData.organization_id) {
      console.error('Missing organization_id in parent data');
      toast.error('Failed to create parent: Missing organization ID');
      return null;
    }
    
    // If creating as primary, first update existing primary parents
    if (parentData.is_primary === true) {
      console.log('🔄 DB: Creating primary parent, removing existing primary status...');
      console.log('🔄 DB: family_id:', parentData.family_id);
      
      // Update all other primary parents in this family to not be primary
      const { error: updateOthersError } = await supabase
        .from('parents')
        .update({ is_primary: false })
        .eq('family_id', parentData.family_id)
        .eq('is_primary', true);
        
      if (updateOthersError) {
        console.error('❌ DB: Error updating other primary parents:', updateOthersError);
        // Continue with creating the parent even if this fails
      } else {
        console.log('✅ DB: Successfully removed existing primary status');
      }
    }
    
    // Format the phone number before saving
    const formattedPhone = parentData.phone ? formatPhoneNumber(parentData.phone) : undefined;
    
    const { data, error } = await supabase
      .from('parents')
      .insert([{
        family_id: parentData.family_id,
        organization_id: parentData.organization_id,
        first_name: parentData.first_name,
        last_name: parentData.last_name,
        email: parentData.email,
        phone: formattedPhone,
        is_primary: parentData.is_primary,
        invite_status: parentData.invite_status || 'pending',
        user_id: parentData.user_id
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ DB: Error creating parent:', error);
      throw error;
    }
    
    console.log("✅ DB: Parent created successfully:", data);
    console.log("✅ DB: New parent is_primary:", data.is_primary);
    const mappedParent = mapParentFromSupabase(data);
    console.log("✅ DB: Mapped parent is_primary:", mappedParent.is_primary);
    return mappedParent;
  } catch (error) {
    console.error('Error creating parent:', error);
    toast.error('Failed to create parent');
    return null;
  }
};

export const updateParent = async (
  parentId: string,
  updates: Partial<Parent>
): Promise<Parent | null> => {
  try {
    // Format phone number if it's being updated
    const formattedUpdates = { ...updates };
    if (updates.phone) {
      formattedUpdates.phone = formatPhoneNumber(updates.phone);
    }
    
    // If updating to primary, first get the family_id and update other primary parents
    if (updates.is_primary === true) {
      // Get the parent's family_id
      const { data: parentData, error: fetchError } = await supabase
        .from('parents')
        .select('family_id')
        .eq('id', parentId)
        .single();
        
      if (fetchError) {
        console.error('Error fetching parent family:', fetchError);
        throw fetchError;
      }
      
      if (parentData?.family_id) {
        // Update all other primary parents in this family to not be primary
        const { error: updateOthersError } = await supabase
          .from('parents')
          .update({ is_primary: false })
          .eq('family_id', parentData.family_id)
          .eq('is_primary', true)
          .neq('id', parentId);
          
        if (updateOthersError) {
          console.error('Error updating other primary parents:', updateOthersError);
          // Continue with the update even if this fails
        }
      }
    }
    
    const { data, error } = await supabase
      .from('parents')
      .update(formattedUpdates)
      .eq('id', parentId)
      .select();

    if (error) {
      console.error('Error updating parent:', error);
      throw error;
    }
    
    // If no rows were updated (data is empty array), fetch and return the current parent
    if (!data || data.length === 0) {
      const { data: currentParent, error: fetchError } = await supabase
        .from('parents')
        .select()
        .eq('id', parentId)
        .single();
        
      if (fetchError || !currentParent) {
        throw new Error('Parent not found');
      }
      
      return mapParentFromSupabase(currentParent);
    }
    
    return mapParentFromSupabase(data[0]);
  } catch (error) {
    console.error('Error updating parent:', error);
    toast.error('Failed to update parent');
    return null;
  }
};

/**
 * Toggle the active status of a parent
 * @param parentId The ID of the parent to toggle
 * @param isActive The new active status
 * @returns The updated parent or null on error
 */
export const toggleParentActiveStatus = async (
  parentId: string,
  isActive: boolean
): Promise<Parent | null> => {
  try {
    console.log(`Toggling parent active status to ${isActive ? 'active' : 'inactive'} for parent ID: ${parentId}`);
    
    const { data, error } = await supabase
      .from('parents')
      .update({ is_active: isActive })
      .eq('id', parentId)
      .select()
      .single();

    if (error) {
      console.error('Error toggling parent active status:', error);
      throw error;
    }
    
    // If deactivating, we should also revoke their auth tokens if they have a user account
    if (!isActive && data.user_id) {
      try {
        // This requires admin privileges, but keeping the code for future implementation
        console.log('User account associated with parent, would revoke tokens here if admin API available');
        // Would typically call: await supabase.auth.admin.signOut(data.user_id);
      } catch (signOutError) {
        console.error('Error revoking user tokens:', signOutError);
        // Don't throw this error as the parent status was successfully updated
      }
    }
    
    return mapParentFromSupabase(data);
  } catch (error) {
    console.error('Error toggling parent active status:', error);
    toast.error('Failed to update parent status');
    return null;
  }
};

/**
 * Soft delete a parent by setting is_deleted flag
 * @param parentId The ID of the parent to soft delete
 * @returns Success status
 */
export const softDeleteParent = async (parentId: string): Promise<boolean> => {
  try {
    console.log('Soft deleting parent with ID:', parentId);
    
    // First check if the parent exists
    const { data: parentData, error: checkError } = await supabase
      .from('parents')
      .select('id, first_name, last_name, user_id')
      .eq('id', parentId)
      .single();
      
    if (checkError) {
      console.error('Error checking parent existence:', checkError);
      toast.error('Failed to delete parent: Parent not found');
      return false;
    }
    
    if (!parentData) {
      console.error('Parent not found with ID:', parentId);
      toast.error('Failed to delete parent: Parent not found');
      return false;
    }
    
    console.log('Found parent to soft delete:', parentData);
    
    // Mark the parent as deleted
    const { error } = await supabase
      .from('parents')
      .update({
        is_deleted: true,
        is_active: false,
        deleted_at: new Date().toISOString()
      })
      .eq('id', parentId);

    if (error) {
      console.error('Error soft deleting parent:', error);
      toast.error(`Failed to delete parent: ${error.message}`);
      return false;
    }
    
    // If the parent has a user account, we should deactivate it
    if (parentData.user_id) {
      try {
        // This requires admin privileges, but keeping the code for future implementation
        console.log('User account associated with parent would be deactivated here if admin API available');
        // await supabase.auth.admin.updateUserById(parentData.user_id, { user_metadata: { is_deleted: true } });
      } catch (updateError) {
        console.error('Error updating user account:', updateError);
        // Don't return false as the parent record was successfully updated
      }
    }
    
    console.log('Successfully soft deleted parent:', parentData);
    toast.success(`Successfully removed parent ${parentData.first_name} ${parentData.last_name}`);
    return true;
  } catch (error) {
    console.error('Error in softDeleteParent function:', error);
    toast.error('Failed to delete parent: Unexpected error');
    return false;
  }
};

export const deleteParent = async (parentId: string): Promise<boolean> => {
  try {
    console.log('Deleting parent with ID:', parentId);
    
    // First check if the parent exists
    const { data: parentData, error: checkError } = await supabase
      .from('parents')
      .select('id, first_name, last_name')
      .eq('id', parentId)
      .single();
      
    if (checkError) {
      console.error('Error checking parent existence:', checkError);
      toast.error('Failed to delete parent: Parent not found');
      return false;
    }
    
    if (!parentData) {
      console.error('Parent not found with ID:', parentId);
      toast.error('Failed to delete parent: Parent not found');
      return false;
    }
    
    console.log('Found parent to delete:', parentData);
    
    // Attempt to delete the parent record
    const { error } = await supabase
      .from('parents')
      .delete()
      .eq('id', parentId);

    if (error) {
      console.error('Error deleting parent:', error);
      
      // Check for specific error types
      if (error.code === '42501') {
        toast.error('Permission denied: You do not have permission to delete this parent');
      } else if (error.code === '23503') {
        toast.error('Cannot delete parent: This parent has related records');
      } else {
        toast.error(`Failed to delete parent: ${error.message}`);
      }
      
      return false;
    }
    
    console.log('Successfully deleted parent:', parentData);
    toast.success(`Successfully deleted parent ${parentData.first_name} ${parentData.last_name}`);
    return true;
  } catch (error) {
    console.error('Error in deleteParent function:', error);
    toast.error('Failed to delete parent: Unexpected error');
    return false;
  }
};

/**
 * Get the primary parent's email for a family
 * @param familyId The ID of the family
 * @returns The primary parent's email or null if not found
 */
export const getPrimaryParentEmail = async (familyId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('parents')
      .select('email')
      .eq('family_id', familyId)
      .eq('is_primary', true)
      .limit(1)
      .single();

    if (error) {
      console.error('Error fetching primary parent email:', error);
      return null;
    }
    
    return data?.email || null;
  } catch (error) {
    console.error('Error in getPrimaryParentEmail:', error);
    return null;
  }
};
